// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useIsSmall } from '@hooks/other/useIsSmall';
import React, { PropsWithChildren } from 'react';
import { Box, Button, ButtonProps, Typography } from '@mui/material';

interface PageWrapperProps {
  buttonLabel?: string;
  onClick?: () => void;
  pageName: string | JSX.Element;
  buttons?: ButtonProps[];
}

export const PageWrapper: React.FC<PropsWithChildren<PageWrapperProps>> = ({
  buttonLabel,
  onClick,
  pageName,
  children,
  buttons,
}) => {
  const isSmall = useIsSmall();
  const pageNameDisplay =
    typeof pageName === 'string' ? (
      <Typography
        sx={{
          display: 'flex',
          flex: 1,
          textTransform: 'capitalize',
        }}
        fontWeight="bold"
        color="primary"
        variant="h4"
      >
        {pageName}
      </Typography>
    ) : (
      pageName
    );
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Box
        sx={{
          flexWrap: 'wrap',
          display: 'flex',
          flexDirection: 'row',
          pt: isSmall ? 0 : 2,
          mb: 1,
          alignItems: 'center',
        }}
      >
        {!isSmall && pageNameDisplay}
        <Box
          sx={{
            display: 'flex',
            flexGrow: isSmall ? 1 : 0,
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'wrap',
            overflowX: 'auto',
            rowGap: '10px',
          }}
        >
          {buttons?.map((b) => (
            <Button
              sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', mr: 1 }}
              fullWidth={isSmall}
              size="small"
              key={b.name}
              onClick={b.onClick}
              disabled={b.disabled}
            >
              {b.name}
            </Button>
          ))}
        </Box>
        {onClick && (
          <Button sx={{ ml: 'auto' }} onClick={onClick} size="small">
            {buttonLabel}
          </Button>
        )}
      </Box>
      <Box sx={{ overflow: 'auto', display: 'flex', width: '100%', flexDirection: 'column' }}>
        {children}
      </Box>
    </Box>
  );
};
